<template>
  <div class="app-shell">
    <app-navigation-drawer
      :navigation-drawer-open.sync="navigationDrawerOpen"
      :navigation-drawer-mini="navigationDrawerMini"
      :is-admin="isAdmin"
      :setup-progress="setupProgress"
      :show-setup-card="showSetupCard"
      :saved-searches-drawer-open="savedSearchesDrawerOpen"
      @toggle-navigation-drawer="toggleNavigationDrawer"
      @toggle-saved-searches-drawer="toggleSavedSearchesDrawer"
      @close-saved-searches-drawer="closeSavedSearchesDrawer"
      @open-utility-drawer="onOpenUtilityDrawer"
      @continue-setup="$emit('continue-setup')"
    />

    <div class="app-shell__main">
      <app-header
        :user-picture="userPicture"
        @ask-tracey="$emit('ask-tracey')"
        @whats-new="$emit('whats-new')"
        @open-profile="$emit('open-profile')"
        @search="$emit('search', $event)"
      >
        <template v-if="$slots.search" #search>
          <slot name="search" />
        </template>
      </app-header>

      <!-- pb-0 only: pa-0/px-0 would override Vuetify's padding for the app bar and navigation drawer -->
      <v-main class="app-shell__content pb-0">
        <slot />
      </v-main>
    </div>

    <saved-searches-drawer
      ref="savedSearchesDrawer"
      :should-show="true"
      :navigation-drawer-open="navigationDrawerOpen"
      :navigation-drawer-mini="navigationDrawerMini"
      :right-drawer-open="rightDrawerOpen"
      @open-change="onSavedSearchesOpenChange"
      @close-utility-drawers="$emit('close-utility-drawers')"
    />
  </div>
</template>

<script>
import AppNavigationDrawer from './AppNavigationDrawer.vue';
import AppHeader from './AppHeader.vue';
import SavedSearchesDrawer from './SavedSearchesDrawer.vue';
import { bus } from '@/main.js';

const NAV_DRAWER_MINI_KEY = 'tt_nav_drawer_mini';

/** Read the persisted nav-drawer collapsed state (best-effort; storage may be unavailable). */
function readNavDrawerMini() {
  try {
    return localStorage.getItem(NAV_DRAWER_MINI_KEY) === 'true';
  } catch (e) {
    return false;
  }
}

export default {
  name: 'AppShell',

  components: {
    AppNavigationDrawer,
    AppHeader,
    SavedSearchesDrawer,
  },

  props: {
    isAdmin: {
      type: Boolean,
      default: false,
    },
    setupProgress: {
      type: Number,
      default: 0,
    },
    showSetupCard: {
      type: Boolean,
      default: true,
    },
    userPicture: {
      type: String,
      default: null,
    },
    rightDrawerOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      navigationDrawerOpen: true,
      // Restore the collapsed (mini) state the user last chose so the nav drawer
      // doesn't spring back open on every reload.
      navigationDrawerMini: readNavDrawerMini(),
      savedSearchesDrawerOpen: false,
    };
  },

  mounted() {
    this.onHideSideNavs = () => {
      this.closeSavedSearchesDrawer();
    };
    bus.$on('hide-side-navs', this.onHideSideNavs);
  },

  beforeDestroy() {
    bus.$off('hide-side-navs', this.onHideSideNavs);
  },

  watch: {
    $route() {
      this.closeSavedSearchesDrawer();
    },

    rightDrawerOpen(isOpen) {
      if (isOpen) {
        this.closeSavedSearchesDrawer();
      }
    },
  },

  methods: {
    toggleNavigationDrawer() {
      if (this.$vuetify.breakpoint.mdAndUp) {
        this.navigationDrawerMini = !this.navigationDrawerMini;
        // Persist so the choice survives reloads, and broadcast so fixed-layout
        // pages (e.g. /tracey) can re-inset for the new drawer width.
        try {
          localStorage.setItem(NAV_DRAWER_MINI_KEY, String(this.navigationDrawerMini));
        } catch (e) {
          /* storage unavailable — non-fatal */
        }
      } else {
        this.navigationDrawerOpen = !this.navigationDrawerOpen;
      }
      bus.$emit('nav-drawer-changed', {
        open: this.navigationDrawerOpen,
        mini: this.navigationDrawerMini,
      });
      this.$emit('toggle-navigation-drawer', {
        open: this.navigationDrawerOpen,
        mini: this.navigationDrawerMini,
      });
    },
    toggleSavedSearchesDrawer() {
      this.$refs.savedSearchesDrawer?.toggle();
    },
    closeSavedSearchesDrawer() {
      this.$refs.savedSearchesDrawer?.close();
      this.savedSearchesDrawerOpen = false;
    },
    onSavedSearchesOpenChange(isOpen) {
      this.savedSearchesDrawerOpen = isOpen;
    },
    onOpenUtilityDrawer(drawerTab) {
      this.$emit('open-utility-drawer', drawerTab);
    },
  },
};
</script>

<style scoped>
.app-shell {
  display: contents;
}

.app-shell__main {
  min-height: 100vh;
  background: #ffffff;
}

.app-shell__content {
  background: #ffffff;
}
</style>
